import { defineStore } from "pinia";
import { getMe, getMyRating, resetPlayerAvatar, updatePlayerAvatar, updatePlayerName } from "@/services/api";
import { normalizeMode, normalizePlayer, normalizeRatingRow } from "@/utils/player";

/**
 * Сессия игрока: токен, профиль, настройки (pinia).
 *
 * state:
 *   token                 — JWT (только в памяти; при старте приложения сбрасывается)
 *   me                    — PlayerInfo (см. utils/player.normalizePlayer) или null
 *   myRating              — RatingRow или null
 *   mode                  — 'BLINK' | 'MOUTH' (localStorage 'cb_mode')
 *   agreementAccepted     — boolean (localStorage 'cb_agreement_v2' === 'accepted')
 *   notificationsEnabled  — boolean (localStorage 'cb_notifications', по умолчанию true)
 *   meLoading / meError   — состояние загрузки профиля
 *
 * getters: userId, stars, displayName
 * actions: setToken, loadMe, loadMyRating, setMode, acceptAgreement, revokeAgreement,
 *          setNotifications, toggleNotifications, rename, updateAvatar, resetAvatar, setStars, patchMe
 */

export const STORAGE_KEYS = Object.freeze({
  mode: "cb_mode",
  agreement: "cb_agreement_v2",
  notifications: "cb_notifications",
});

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (e) {
    // приватный режим / хранилище недоступно — настройка просто не сохранится
  }
}

export const useSessionStore = defineStore("session", {
  state: () => ({
    token: null,
    me: null,
    myRating: null,
    mode: normalizeMode(readStorage(STORAGE_KEYS.mode)),
    agreementAccepted: readStorage(STORAGE_KEYS.agreement) === "accepted",
    notificationsEnabled: readStorage(STORAGE_KEYS.notifications) !== "off",
    meLoading: false,
    meError: null,
  }),

  getters: {
    userId: (state) => (state.me ? state.me.userId : null),
    stars: (state) => (state.me ? state.me.stars : 0),
    displayName: (state) => (state.me ? state.me.playerName : ""),
  },

  actions: {
    /** Сохранить JWT после авторизации. */
    setToken(token) {
      this.token = token || null;
    },

    /**
     * Загрузить профиль (GET get-current-player-info).
     * @returns {Promise<object|null>} PlayerInfo; при ошибке бросает ApiError и пишет meError
     */
    async loadMe() {
      this.meLoading = true;
      this.meError = null;
      try {
        this.me = normalizePlayer(await getMe());
        return this.me;
      } catch (error) {
        this.meError = error;
        throw error;
      } finally {
        this.meLoading = false;
      }
    },

    /** Загрузить мою строку рейтинга (GET my-rating). Ошибки не бросает (старый bunker). */
    async loadMyRating() {
      try {
        this.myRating = normalizeRatingRow(await getMyRating());
      } catch (error) {
        this.myRating = null;
      }
      return this.myRating;
    },

    /** Выбрать режим игры и запомнить его. */
    setMode(mode) {
      this.mode = normalizeMode(mode);
      writeStorage(STORAGE_KEYS.mode, this.mode);
    },

    /** Принять соглашение (запоминается). */
    acceptAgreement() {
      this.agreementAccepted = true;
      writeStorage(STORAGE_KEYS.agreement, "accepted");
    },

    /** Отозвать согласие. */
    revokeAgreement() {
      this.agreementAccepted = false;
      writeStorage(STORAGE_KEYS.agreement, null);
    },

    /** Включить/выключить уведомления (только локальная настройка). */
    setNotifications(enabled) {
      this.notificationsEnabled = !!enabled;
      writeStorage(STORAGE_KEYS.notifications, this.notificationsEnabled ? "on" : "off");
    },

    toggleNotifications() {
      this.setNotifications(!this.notificationsEnabled);
      return this.notificationsEnabled;
    },

    /**
     * Сменить имя (POST update-player-name). Обновляет me.
     * @throws {ApiError} code 'INVALID_NAME' при невалидном имени
     */
    async rename(name) {
      const updated = normalizePlayer(await updatePlayerName(String(name || "").trim()));
      if (updated) this.me = { ...(this.me || {}), ...updated };
      return this.me;
    },

    /**
     * Загрузить свой аватар (POST update-player-avatar). Обновляет me.
     * @param {Blob} image — подготовленная картинка (utils/image.prepareAvatar)
     * @throws {ApiError} INVALID_AVATAR | FILE_TOO_LARGE | AVATAR_UPLOAD_FAILED
     */
    async updateAvatar(image) {
      const updated = normalizePlayer(await updatePlayerAvatar(image));
      if (updated) this.me = { ...(this.me || {}), ...updated };
      return this.me;
    },

    /** Убрать свой аватар (POST reset-player-avatar) — снова фото из Telegram. Обновляет me. */
    async resetAvatar() {
      const updated = normalizePlayer(await resetPlayerAvatar());
      if (updated) this.me = { ...(this.me || {}), ...updated };
      return this.me;
    },

    /** Обновить баланс звёзд (например, из FINAL-сообщения). */
    setStars(stars) {
      if (this.me && Number.isFinite(Number(stars))) this.me = { ...this.me, stars: Number(stars) };
    },

    /** Частично обновить профиль локально (wins/losses после игры и т.п.). */
    patchMe(patch) {
      if (this.me) this.me = { ...this.me, ...patch };
    },
  },
});
